import { Components, Enums, Types } from '@sendum/core'
import React, { useCallback, useMemo, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { AnyAction } from 'redux'

import { refreshPallets, setPallet } from '../store/PalletSelectorActions'
import { PalletSelectorRootState } from '../store/PalletSelectorStore'
import { calculateAlarmDescription } from '../utils'
import { ConfirmSetDriver, IConfirmSetDriverRef } from './ConfirmSetDriverModal'
import { IMoveModalRef, MoveModal } from './MoveModal'
import cn from './Pallets.module.scss'

type TableData = {
    PalletId: string
    BatchNumber: string
    PalletNumber: string
    LocationName: string | null
    Tor: number
    TorMax: number
    Cpd: number
    Alarm: string
    Status: Enums.PalletState
    ShipmentId: string
    PalletPhysicalID: string | null
    Cart: string
    AssignedDriverName: string
}

interface IProps {
    className?: string
    isMovement?: boolean
}

export const Pallets = ({ className, isMovement }: IProps): JSX.Element => {
    const dispatch = useDispatch()
    const pallets: Types.Pallet[] = useSelector<PalletSelectorRootState, Types.Pallet[]>(
        (state: PalletSelectorRootState) => state.PalletSelector.pallets,
        shallowEqual
    )
    const pallet = useSelector<PalletSelectorRootState, Types.Pallet | undefined>(
        (state: PalletSelectorRootState) => state.PalletSelector.pallet,
        shallowEqual
    )
    const account = useSelector<PalletSelectorRootState, Types.Account | undefined>(
        (state: PalletSelectorRootState) => state.PalletSelector.account,
        shallowEqual
    )
    const moveModalRef = useRef<IMoveModalRef>(null)
    const confirmModalRef = useRef<IConfirmSetDriverRef>(null)

    const renderTimeCell = ({
        value
    }: Components.TableRowCellData<TableData, number | undefined>): JSX.Element | null => {
        if (!value) {
            return (
                <div className={cn.Pallets__timeCell}>
                    0<span>:</span>00
                </div>
            )
        }

        const h = Math.trunc(value / 60)
        const m = value - h * 60

        return (
            <div className={cn.Pallets__timeCell}>
                {h}
                <span>:</span>
                {m < 10 ? `0${m}` : m}
            </div>
        )
    }

    const columns: Components.TableColumn<TableData>[] = [
        { accessor: 'BatchNumber', title: 'Batch Number', titleAlign: 'center', minWidth:8, maxWidth: 10, widthUnits: '%', width: 10},
        { accessor: 'PalletNumber', title: 'Logical Pallet Number', titleAlign: 'center', minWidth: 9, maxWidth:10, widthUnits: '%', width: 10 },
        { accessor: 'PalletPhysicalID', title: 'Physical Pallet Number', titleAlign: 'center', minWidth:14, maxWidth: 18, widthUnits: '%', },
        { accessor: 'Cart', title: 'Cart(s)', titleAlign: 'center',  maxWidth: 10,  width: 10, widthUnits: '%' },
        { accessor: 'LocationName', title: 'Location', titleAlign: 'center',  maxWidth: 10, widthUnits: '%', width: 10 },
        { accessor: 'AssignedDriverName', title: 'Assigned Driver', titleAlign: 'center', minWidth: 9, maxWidth: 16, width: 16, widthUnits: '%' },
        { accessor: 'Tor', title: 'TOR (h:mm)', Cell: renderTimeCell, titleAlign: 'center',  maxWidth: 8, widthUnits: '%', width: 8 },
        { accessor: 'Cpd', title: 'CPD (h:mm)', Cell: renderTimeCell, titleAlign: 'center',  maxWidth: 8, widthUnits: '%', width: 8 },
        { accessor: 'Alarm', title: 'Alarm description', titleAlign: 'center',  maxWidth: 10, widthUnits: '%', width: 10 }
    ]

    const data = useMemo<TableData[]>(
        () =>
            pallets.map(item => ({
                PalletId: item.PalletId,
                BatchNumber: item.Batch.BatchNumber,
                PalletNumber: item.PalletNumber ? item.PalletNumber.toString() : 'No Logical Pallets',
                LocationName: item.Location.Label ?? item.Location.LocationName,
                AssignedDriverName: item.AssignedDriver?.DisplayName ?? '',
                Tor: item.Tor,
                TorMax: item.Product.TorMax,
                Cpd: item.Cpd,
                Alarm: calculateAlarmDescription(item),
                Status: item.CurrentState,
                ShipmentId: item.ShipmentId,
                PalletPhysicalID: item.PalletPhysicalID,
                Cart: [item.Cart1, item.Cart2].filter(_ => !!_).join(',')
            })),
        [pallets]
    )

    const handleOnClickRow = useCallback(
        (item: TableData) => {
            const p = pallets.find(_ => _.PalletId === item.PalletId)
            dispatch(setPallet(p))
            if (p && !!account && account.Role !== Types.AccountRole.VIEW_ONLY_USER && isMovement) {
                if (
                    (p.AssignedDriver?.UserName !== undefined && p.AssignedDriver?.UserName === account?.UserName) ||
                    account?.Role === Types.AccountRole.ADMIN
                ) {
                    moveModalRef.current?.open(p)
                } else {
                    confirmModalRef.current?.open(p)
                }
            }
        },
        [dispatch, pallets, isMovement, account]
    )

    const rowStyle = useCallback(
        (item: TableData): string => {
            const activeClass = !!pallet && item.PalletId === pallet.PalletId ? cn.active : ''
            const viewOnlyClass =
                isMovement && account && account.Role === Types.AccountRole.VIEW_ONLY_USER ? cn.viewOnly : ''

            if (item.Status === Enums.PalletState.Alarm) {
                return `${cn.Pallets__alarmRow} ${activeClass} ${viewOnlyClass}`
            }

            if (item.Status === Enums.PalletState.Warning) {
                return `${cn.Pallets__warningRow} ${activeClass} ${viewOnlyClass}`
            }

            return `${cn.Pallets__tableRow} ${activeClass} ${viewOnlyClass}`
        },
        [pallet, isMovement, account]
    )

    const onMoved = useCallback(() => {
        dispatch((refreshPallets(isMovement) as unknown) as AnyAction)
    }, [dispatch, isMovement])

    const onConfirmed = useCallback(
        (palletId: string) => {
            dispatch((refreshPallets(isMovement) as unknown) as AnyAction)
            const p = pallets.find(_ => _.PalletId === palletId)
            if (p) {
                moveModalRef.current?.open(p)
            }
        },
        [dispatch, pallets, isMovement]
    )

    return (
        <div className={`${className ?? ''} ${cn.Pallets} `}>
            <Components.Table
                className={cn.Pallets__tableContainer}
                data={data}
                columns={columns}
                onClickRow={handleOnClickRow}
                rowStyle={rowStyle}
            />

            <MoveModal ref={moveModalRef} onMoved={onMoved} isMovement={isMovement} />
            <ConfirmSetDriver ref={confirmModalRef} onConfirmed={onConfirmed} />
        </div>
    )
}
